// Read-only preview of a transaction sql_model.
//
// Same visual language as the editor's PreviewPanel.tsx (form mode + grid
// mode), but without the editor-only affordances (drag/drop, resize, click-
// to-select). Used inside the page designer's EmbedPreview so an embedded
// transaction renders the way it looks in the object editor.

import { useMemo, useState } from 'react'
import type { ColumnDef, SqlModel } from './types'
import { actionName } from './types'
import {
  calendarDefaultForInput, isRichTextColumn, hasTextarea, hasUrlWidget, hasImageUpload, hasDocumentUpload,
} from '../../../livepreview/utils'
import MacCalendarIcon from '../../../livepreview/runtime/widgets/MacCalendarIcon'

const DEFAULT_COL_WIDTH = 160

// ─── Shared helpers (kept identical to PreviewPanel.tsx) ───────────────

export function isHidden(col: ColumnDef): boolean {
  const h = col.hidden
  return h === true || h === 'true' || h === '1' || (h as unknown) === 1
}

export function isProtected(col: ColumnDef): boolean {
  const p = col.protect
  return p === true || p === 'true' || p === '1' || (p as unknown) === 1
}

export function isMandatory(col: ColumnDef): boolean {
  const m = col.mandatory
  return m === true || m === 'true' || m === '1' || (m as unknown) === 1
}

export function formatGroupName(name: string): string {
  if (name === 'Others') return 'Other Information'
  return name || 'Other Information'
}

export function formatColumnLabel(col: ColumnDef): string {
  if (col.heading) return col.heading
  // No heading set — fall back to the column name with underscores
  // turned into spaces. Case is preserved as authored; we don't
  // title-case here because the designer may use names like "ID" or
  // "URL" whose casing carries meaning.
  return (col.name || '').replace(/_/g, ' ')
}

function getInputType(col: ColumnDef): string {
  switch ((col.col_type || '').toUpperCase()) {
    case 'NUMBER': return 'number'
    case 'DATE': return 'date'
    case 'DATETIME': return 'datetime-local'
    case 'BOOLEAN': return 'checkbox'
    default: return 'text'
  }
}

function resolveDefault(col: ColumnDef, inputType: string): string {
  if (!col.default_value) return ''
  const calVal = calendarDefaultForInput(col.default_value, inputType)
  if (calVal !== null) return calVal
  if (col.default_value.toLowerCase() === 'now()') {
    if (inputType === 'date') return new Date().toISOString().slice(0, 10)
    if (inputType === 'datetime-local') return new Date().toISOString().slice(0, 16)
    return col.default_value
  }
  return col.default_value
}

// ─── Field input (for form view) ───────────────────────────────────────

export function FieldInput({ col }: { col: ColumnDef }) {
  const inputType = getInputType(col)
  const prot = isProtected(col)
  // Editable controls get the elevated input bg (white in light theme)
  // so the user can see at a glance which fields they can type into.
  // Protected controls keep the muted panel bg.
  const editableBg = 'var(--lp-input-bg)'
  const protectedBg = 'var(--color-bg-secondary)'

  if (col.lookup || col.edit_mask?.mask === 'ddlb') {
    return (
      <select
        disabled={prot}
        style={{
          width: '100%', padding: '4px 8px', fontSize: '0.9375rem',
          background: prot ? protectedBg : editableBg,
          border: '1px solid var(--color-border)',
          borderRadius: 4, color: 'var(--color-text)',
        }}
      >
        <option value="">Select…</option>
        {Array.isArray(col.edit_mask?.values) && col.edit_mask.values.map((v, i) => (
          <option key={i} value={v.data}>{v.display}</option>
        ))}
      </select>
    )
  }

  if (col.edit_mask?.mask === 'bool_check' || col.edit_mask?.mask === 'checkbox' || inputType === 'checkbox') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30 }}>
        <input type="checkbox" disabled={prot} />
      </div>
    )
  }

  if (col.edit_mask?.mask === 'radio') {
    const opts = col.edit_mask.values || [
      { data: 'yes', display: 'Yes' },
      { data: 'no', display: 'No' },
    ]
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {opts.map((v, i) => (
          <label key={i} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: '0.9375rem', color: 'var(--color-text)',
          }}>
            <input type="radio" name={col.name} disabled={prot} />
            {v.display}
          </label>
        ))}
      </div>
    )
  }

  const baseInputStyle = {
    width: '100%', padding: '4px 8px', fontSize: '0.9375rem',
    background: prot ? protectedBg : editableBg,
    border: '1px solid var(--color-border)',
    borderRadius: 4, color: 'var(--color-text)', opacity: prot ? 0.6 : 1,
  } as const

  if (isRichTextColumn(col)) {
    return (
      <div style={{ ...baseInputStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', gap: 10, padding: '4px 8px', fontSize: '0.8125rem', fontWeight: 600,
          color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
        }}>
          <span>Paragraph ▾</span><b>B</b><i>I</i><u>U</u><span>• List</span><span>🔗</span><span>🖼</span><span>▦</span>
        </div>
        <div style={{ minHeight: 64, padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
          {col.descr || 'Rich text — headings, lists, links, tables and images'}
        </div>
      </div>
    )
  }
  if (hasTextarea(col)) {
    return (
      <textarea
        placeholder={col.descr || col.heading}
        disabled={prot}
        rows={2}
        style={{ ...baseInputStyle, resize: 'none', minHeight: 48 }}
      />
    )
  }
  if (hasUrlWidget(col)) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input type="text" placeholder="https://…" disabled={prot} style={{ ...baseInputStyle, flex: 1 }} />
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px',
          border: '1px solid var(--color-border)', borderRadius: 4,
          color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary)', fontSize: '0.8125rem',
        }}>↗</span>
      </div>
    )
  }
  if (hasImageUpload(col) || hasDocumentUpload(col)) {
    const isImg = hasImageUpload(col)
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        border: '1px dashed var(--color-border)', borderRadius: 6,
        background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', fontSize: '0.8425rem',
      }}>
        {isImg ? '🖼' : '📄'} {isImg ? 'Add image' : 'Add document'}
      </div>
    )
  }
  if (inputType === 'date' || inputType === 'datetime-local') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MacCalendarIcon value={col.default_value} size={30} />
        <input type={inputType} placeholder={col.descr || col.heading} disabled={prot}
          defaultValue={resolveDefault(col, inputType)} style={{ ...baseInputStyle, flex: 1 }} />
      </div>
    )
  }

  return (
    <input
      type={inputType}
      placeholder={col.descr || col.heading}
      disabled={prot}
      defaultValue={resolveDefault(col, inputType)}
      style={baseInputStyle}
    />
  )
}

// ─── Grid cell (for list view) ─────────────────────────────────────────

export function GridCell({ col }: { col: ColumnDef }) {
  if (isProtected(col)) {
    return <span style={{ color: 'var(--color-text-secondary)' }}>[{col.heading || col.name}]</span>
  }
  if (col.lookup || col.edit_mask?.mask === 'ddlb') {
    return (
      <select style={{
        width: '100%', padding: '2px 4px', fontSize: '0.9375rem',
        background: 'var(--lp-input-bg)', border: '1px solid var(--color-border)',
        borderRadius: 3, color: 'var(--color-text)',
      }}>
        <option value="">Select…</option>
        {Array.isArray(col.edit_mask?.values) && col.edit_mask.values.map((v, i) => (
          <option key={i} value={v.data}>{v.display}</option>
        ))}
      </select>
    )
  }
  if (col.edit_mask?.mask === 'bool_check') {
    return <input type="checkbox" />
  }
  if (col.edit_mask?.mask === 'radio') {
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(col.edit_mask.values || []).map((v, i) => (
          <label key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.9125rem', color: 'var(--color-text)' }}>
            <input type="radio" name={`grid-${col.name}`} />
            {v.display || v.data}
          </label>
        ))}
      </div>
    )
  }
  const inputType = getInputType(col)
  return (
    <input
      type={inputType === 'checkbox' ? 'text' : inputType}
      placeholder={col.descr || ''}
      defaultValue={resolveDefault(col, inputType)}
      style={{
        width: '100%', padding: '2px 4px', fontSize: '0.9375rem',
        background: 'var(--lp-input-bg)', border: '1px solid var(--color-border)',
        borderRadius: 3, color: 'var(--color-text)',
      }}
    />
  )
}

// ─── Headers / actions ────────────────────────────────────────────────

function FormHeader({ form, headerActions }: { form: SqlModel; headerActions?: React.ReactNode }) {
  return (
    <div style={{
      padding: '12px 16px',
      background: 'var(--color-bg-secondary)',
      borderRadius: '8px 8px 0 0',
      border: '1px solid var(--color-border)',
      borderBottom: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={{
          fontSize: '1.0625rem', fontWeight: 600, margin: 0,
          color: 'var(--color-text)', flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {form.form_title || 'Untitled Form'}
        </h3>
        {headerActions && <div style={{ flexShrink: 0 }}>{headerActions}</div>}
      </div>
      {form.form_descr && (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '4px 0 0 0' }}>
          {form.form_descr}
        </p>
      )}
    </div>
  )
}

function ActionStrip({ form }: { form: SqlModel }) {
  if (!form.actions || form.actions.length === 0) return null
  return (
    <div style={{
      display: 'flex', gap: 6, padding: '8px 16px',
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)', borderBottom: 'none', flexWrap: 'wrap',
    }}>
      {form.actions.map((a, i) => {
        const name = actionName(a)
        return (
          <button
            key={i}
            onClick={(e) => e.preventDefault()}
            style={{
              padding: '4px 12px', fontSize: '0.8125rem', borderRadius: 12,
              border: '1px solid var(--color-accent)', background: 'transparent',
              color: 'var(--color-accent)', cursor: 'pointer',
            }}
          >{name}</button>
        )
      })}
    </div>
  )
}

// ─── Form view (add / edit) ───────────────────────────────────────────

function GroupBlock({ groupName, columns }: {
  groupName: string
  columns: ColumnDef[]
}) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid var(--color-border)' }}>
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          padding: '6px 16px', fontSize: '0.8625rem', fontWeight: 600,
          color: 'var(--color-text-secondary)',
          background: 'var(--color-bg-secondary)',
          cursor: 'pointer', userSelect: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ fontSize: '0.8125rem' }}>{collapsed ? '▶' : '▼'}</span>
        {formatGroupName(groupName)}
      </div>
      {!collapsed && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '8px 16px',
          padding: '8px 16px',
        }}>
          {columns.map((col) => (
            <div key={col.name} style={{ padding: '4px 6px' }}>
              <label style={{
                display: 'block', fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)', marginBottom: 2,
              }}>
                {formatColumnLabel(col)}
                {isMandatory(col) && <span style={{ color: 'var(--color-error, #cf222e)', marginLeft: 2 }}>*</span>}
                {col.key && <span style={{ color: 'var(--color-warning, #9a6700)', marginLeft: 4, fontSize: '0.8125rem' }}>PK</span>}
              </label>
              <FieldInput col={col} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function FormPreviewView({ form, headerActions }: { form: SqlModel; headerActions?: React.ReactNode }) {
  const visible = useMemo(
    () => (form.columns || []).map((c) => c.column).filter((c) => !isHidden(c)),
    [form.columns]
  )

  // Group by group_name, sort columns within group by y, sort groups
  const groups = useMemo(() => {
    const map = new Map<string, ColumnDef[]>()
    for (const col of visible) {
      const g = col.group_name || 'Others'
      if (!map.has(g)) map.set(g, [])
      map.get(g)!.push(col)
    }
    map.forEach((cols) => {
      cols.sort((a, b) => (Number(a.y) || 0) - (Number(b.y) || 0))
    })
    const entries = [...map.entries()]
    entries.sort((a, b) => {
      if (a[0] === 'Basic Information' || a[0] === 'Basic') return -1
      if (b[0] === 'Basic Information' || b[0] === 'Basic') return 1
      if (a[0] === 'Others' || a[0] === 'Other Information') return 1
      if (b[0] === 'Others' || b[0] === 'Other Information') return -1
      return a[0].localeCompare(b[0])
    })
    return entries
  }, [visible])

  return (
    <div>
      <FormHeader form={form} headerActions={headerActions} />
      <ActionStrip form={form} />
      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: '0 0 8px 8px',
        background: 'var(--color-bg)',
        overflow: 'hidden',
      }}>
        {groups.map(([groupName, cols]) => (
          <GroupBlock key={groupName} groupName={groupName} columns={cols} />
        ))}
        {visible.length === 0 && (
          <div style={{
            padding: 24, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
          }}>No columns defined.</div>
        )}
      </div>
    </div>
  )
}

// ─── Grid (list) view ────────────────────────────────────────────────

export function GridPreviewView({ form, sampleRows = 3, headerActions }: {
  form: SqlModel
  sampleRows?: number
  headerActions?: React.ReactNode
}) {
  const visible = useMemo(
    () => (form.columns || [])
      .map((c) => c.column)
      .filter((c) => !isHidden(c))
      .sort((a, b) => (Number(a.x) || 0) - (Number(b.x) || 0)),
    [form.columns]
  )

  return (
    <div>
      <FormHeader form={form} headerActions={headerActions} />
      <ActionStrip form={form} />
      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: '0 0 8px 8px',
        overflowX: 'auto',
        overflowY: 'hidden',
        background: 'var(--color-bg)',
      }}>
        <table style={{
          borderCollapse: 'collapse',
          fontSize: '0.9625rem',
          tableLayout: 'fixed',
          // No width:100% — colgroup widths drive natural width.
          // minWidth:100% fills the container when columns are narrow.
          minWidth: '100%',
        }}>
          <colgroup>
            {visible.map((col, i) => (
              <col key={i} style={{ width: `${col.width || DEFAULT_COL_WIDTH}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visible.map((col) => {
                const w = col.width || DEFAULT_COL_WIDTH
                return (
                  <th
                    key={col.name}
                    style={{
                      padding: '10px 12px', textAlign: 'left',
                      fontWeight: 600, fontSize: '0.8625rem',
                      // Override global `table th { text-transform: uppercase }`
                      // so author-supplied headings render in their authored case.
                      textTransform: 'none',
                      letterSpacing: 'normal',
                      color: 'var(--color-text)',
                      background: 'var(--color-bg-secondary)',
                      borderBottom: '2px solid var(--color-border)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: `${w}px`,
                    }}
                  >
                    {formatColumnLabel(col)}
                    {isMandatory(col) && <span style={{ color: 'var(--color-error, #cf222e)', marginLeft: 2 }}>*</span>}
                    {col.key && <span style={{ color: 'var(--color-warning, #9a6700)', marginLeft: 4, fontSize: '0.8125rem' }}>PK</span>}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: sampleRows }).map((_, row) => (
              <tr key={row} style={{ borderBottom: '1px solid var(--color-border)' }}>
                {visible.map((col) => (
                  <td
                    key={col.name}
                    style={{
                      padding: '8px 12px',
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.9375rem',
                      background: 'var(--color-bg)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <GridCell col={col} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div style={{
            padding: 24, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
          }}>No columns defined.</div>
        )}
      </div>
    </div>
  )
}
